import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  planTier: 'solo' | 'pro' | 'enterprise';
  primaryColor: string | null;
  isActive: boolean;
  createdAt: string;
  customDomain: string | null;
}

/**
 * /admin/tenants — super_admin directory; lists every tenant in the
 * platform. Super_admin only (Backend enforces via RolesGuard).
 */
export function AdminTenants() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: async () => {
      const r = await api.get<TenantRow[]>('/admin/tenants');
      return r.data;
    },
    enabled: user?.role === 'super_admin',
  });

  if (q.isLoading) return <p className="text-muted-foreground">Loading tenants…</p>;
  if (q.isError) return <p className="text-destructive">Failed to load tenants.</p>;

  return (
    <section className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Tenants directory
        </h1>
      </div>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Custom domain</th>
                <th className="text-left p-3">Active</th>
                <th className="text-left p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="p-3 font-medium">{t.name}</td>
                  <td className="p-3 font-mono text-xs">{t.slug}</td>
                  <td className="p-3 uppercase">{t.planTier}</td>
                  <td className="p-3">{t.customDomain ?? '—'}</td>
                  <td className="p-3">
                    {t.isActive ? '✅' : '⛔'}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {t.createdAt.slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </section>
  );
}